/**
 * PRD.268 — canonical RBAC permission catalog for web-admin.
 *
 * Maps every protected route (and, via the exported constants, every launcher
 * tile / sidebar item) to a CSV `Module | Sub Module` permission requirement.
 *
 * Module / Sub Module strings MUST match the seed CSV
 * (`infra/platform/ansible/files/rbac/role-access.india.csv`) exactly;
 * `permissionKey()` normalizes case/whitespace so headings and UI metadata
 * align. Do not invent module names that are not in the CSV.
 */
import type { PermissionRequirement } from '@helios/authz';

/** CSV Module names. */
export const MODULES = {
  CLINIC_SETTINGS: 'Clinic Settings',
  PATIENT: 'Patient Module',
  INVENTORY: 'Inventory Module',
  APPOINTMENTS: 'Appointments',
  DASHBOARD: 'Dashboard',
  QUEUE: 'Queue Module',
  PATIENT_CREATION: 'Patient Creation',
  PATIENT_CONSULTATION: 'Patient Consultation',
  BILLING: 'Billing / Invoices',
  PHARMACY: 'Pharmacy',
  // PRD.305 — Laboratory Workflow & Patient Labs.
  LABORATORY: 'Laboratory',
  SYNC: 'Sync Option',
} as const;

/** Helper to build a requirement referencing CSV Module/Sub Module strings. */
export function req(
  module: string,
  subModule: string,
  minimumAccess: PermissionRequirement['minimumAccess'] = 'view',
): PermissionRequirement {
  return { module, subModule, minimumAccess };
}

/** Frequently-referenced requirements (shared by routes, nav, and launcher). */
export const PERM = {
  clinicSettings: req(MODULES.CLINIC_SETTINGS, 'All Settings'),
  patientWorkspace: req(MODULES.PATIENT, 'Patient Workspace'),
  patientWorkspaceEdit: req(MODULES.PATIENT, 'Patient Workspace', 'edit'),
  // Editing or deleting an existing patient note. `full`, matching the level
  // Permanent Delete Patient requires: a saved note is another clinician's clinical
  // narrative and there is no soft delete to fall back on — `patient_notes` has no
  // `deleted_at`, so removal is immediate and propagates to every device.
  patientWorkspaceFull: req(MODULES.PATIENT, 'Patient Workspace', 'full'),
  patientProfile: req(MODULES.PATIENT, 'Patient Profile'),
  patientProfileEdit: req(MODULES.PATIENT, 'Patient Profile', 'edit'),
  // PRD.271 — Chief Complaint voice recording & clinical extraction.
  // Access-level maps the PRD capability set onto the CSV model:
  //   Voice Recording:      view = play, edit = record/create, full = delete
  //   Clinical Extraction:  view = view panel, edit = run, full = apply
  voiceRecordingView: req(MODULES.PATIENT, 'Voice Recording'),
  voiceRecordingCreate: req(MODULES.PATIENT, 'Voice Recording', 'edit'),
  voiceRecordingDelete: req(MODULES.PATIENT, 'Voice Recording', 'full'),
  clinicalExtractionView: req(MODULES.PATIENT, 'Clinical Extraction'),
  clinicalExtractionRun: req(MODULES.PATIENT, 'Clinical Extraction', 'edit'),
  clinicalExtractionApply: req(MODULES.PATIENT, 'Clinical Extraction', 'full'),
  // PRD.328 — Permanent Patient Deletion. Its own sub-module rather than reusing
  // `patientProfile` at 'full': irreversible erasure of a patient's entire record
  // should never be implied by the right to edit a profile. Seeded Admin-only.
  permanentDeletePatient: req(MODULES.PATIENT, 'Permanent Delete Patient', 'full'),
  patientForm: req(MODULES.PATIENT, 'Patient Form'),
  intakeForms: req(MODULES.PATIENT, 'Intake Forms'),
  forms: req(MODULES.PATIENT, 'Forms'),
  vitals: req(MODULES.PATIENT, 'Vitals'),
  printSettings: req(MODULES.PATIENT, 'All Print Settings'),
  chartingTemplates: req(MODULES.PATIENT, 'Charting Templates Upload'),
  inventoryItems: req(MODULES.INVENTORY, 'Inventory Items'),
  inventoryAdjustments: req(MODULES.INVENTORY, 'Inventory Adjustments'),
  suppliers: req(MODULES.INVENTORY, 'Suppliers'),
  purchaseOrders: req(MODULES.INVENTORY, 'Purchase Orders'),
  purchaseOrderPrint: req(MODULES.INVENTORY, 'Purchase Order Print'),
  deliveryOrders: req(MODULES.INVENTORY, 'Delivery Orders'),
  stockCard: req(MODULES.INVENTORY, 'Stock Card'),
  patientPackages: req(MODULES.INVENTORY, 'Patient Packages'),
  bookAppointment: req(MODULES.APPOINTMENTS, 'Book a Patient', 'edit'),
  monthView: req(MODULES.APPOINTMENTS, 'Month View'),
  weekView: req(MODULES.APPOINTMENTS, 'Week View'),
  dayView: req(MODULES.APPOINTMENTS, 'Day View'),
  listView: req(MODULES.APPOINTMENTS, 'List View'),
  manageServices: req(MODULES.APPOINTMENTS, 'Manage Services'),
  manageProviders: req(MODULES.APPOINTMENTS, 'Manage Providers'),
  providerAvailability: req(MODULES.APPOINTMENTS, 'Provider Availability'),
  calendarSettings: req(MODULES.APPOINTMENTS, 'Calendar Settings'),
  categoryStatuses: req(MODULES.APPOINTMENTS, 'Category & Statuses'),
  onlineBooking: req(MODULES.APPOINTMENTS, 'Online Booking'),
  frontDeskBooking: req(MODULES.APPOINTMENTS, 'Front Desk Booking Settings'),
  dashboard: req(MODULES.DASHBOARD, 'Dashboard'),
  queue: req(MODULES.QUEUE, 'Queue'),
  createPatient: req(MODULES.PATIENT_CREATION, 'Create Patient', 'edit'),
  consultation: req(MODULES.PATIENT_CONSULTATION, 'Consultation'),
  billing: req(MODULES.BILLING, 'Billing'),
  pharmacy: req(MODULES.PHARMACY, 'Pharmacy Module'),
  // PRD.305 — Laboratory. Access levels map the PRD capability set onto the
  // CSV model, exactly as PRD.271 did for Voice Recording / Clinical Extraction:
  //   Lab Orders:  view = see orders/tests, edit = create, full = cancel/manage
  //   Lab Results: view = read results + open published files,
  //                edit = upload files + advance workflow status,
  //                full = PUBLISH (and record a correction)
  // Laboratory Settings deliberately reuses `clinicSettings` rather than
  // inventing a settings permission — the same choice PRD.302 made for Queue
  // Settings.
  labOrders: req(MODULES.LABORATORY, 'Lab Orders'),
  labOrdersCreate: req(MODULES.LABORATORY, 'Lab Orders', 'edit'),
  labOrdersManage: req(MODULES.LABORATORY, 'Lab Orders', 'full'),
  labResults: req(MODULES.LABORATORY, 'Lab Results'),
  labResultsUpload: req(MODULES.LABORATORY, 'Lab Results', 'edit'),
  labResultsPublish: req(MODULES.LABORATORY, 'Lab Results', 'full'),
  manualSync: req(MODULES.SYNC, 'Manual Sync'),
} as const;

/** Route access categorization. */
export type RouteAccess =
  | { kind: 'permission'; requirement: PermissionRequirement }
  // Satisfied by ANY one of several requirements. For a surface two modules
  // legitimately own — the service catalogue belongs to both Appointments and
  // Laboratory — where requiring both would lock out the staff who need it.
  | { kind: 'anyPermission'; requirements: readonly PermissionRequirement[] }
  | { kind: 'authOnly' } // signed-in + has app access, but not module-gated (e.g. own profile)
  | { kind: 'public' } // no auth required
  | { kind: 'unmapped' }; // no confident CSV mapping — fail closed + dev warn

interface Rule {
  pattern: RegExp;
  access: RouteAccess;
}

const perm = (requirement: PermissionRequirement): RouteAccess => ({
  kind: 'permission',
  requirement,
});
const anyPerm = (...requirements: PermissionRequirement[]): RouteAccess => ({
  kind: 'anyPermission',
  requirements,
});
const AUTH_ONLY: RouteAccess = { kind: 'authOnly' };
const PUBLIC: RouteAccess = { kind: 'public' };
const UNMAPPED: RouteAccess = { kind: 'unmapped' };

/** Turn a route template (`/patient/:id/edit/:group`) into a matcher regex. */
function compile(template: string): RegExp {
  const source = template
    .split('/')
    .map((seg) => (seg.startsWith(':') ? '[^/]+' : escapeRegExp(seg)))
    .join('/');
  return new RegExp(`^${source}/?$`);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Ordered rules — first match wins, so list more-specific static routes
 * before dynamic/prefix routes.
 */
const RULES: Rule[] = [
  // Public / auth-only
  { pattern: compile('/a/:appointmentId'), access: PUBLIC },
  { pattern: compile('/login'), access: PUBLIC },
  { pattern: compile('/settings/profile'), access: AUTH_ONLY },
  { pattern: compile('/clinic/system-info'), access: AUTH_ONLY },

  // Home / dashboard
  { pattern: compile('/'), access: perm(PERM.dashboard) },

  // Clinic settings + patient settings pages
  { pattern: compile('/settings/clinic'), access: perm(PERM.clinicSettings) },
  { pattern: compile('/clinic/sync-status'), access: perm(PERM.manualSync) },
  { pattern: compile('/clinic/intake-form'), access: perm(PERM.intakeForms) },
  { pattern: compile('/clinic/patient-profile'), access: perm(PERM.patientProfile) },
  { pattern: compile('/clinic/patient-form'), access: perm(PERM.patientForm) },
  { pattern: compile('/clinic/vitals-definitions'), access: perm(PERM.vitals) },
  { pattern: compile('/clinic/forms/:formId'), access: perm(PERM.forms) },
  { pattern: compile('/clinic/forms'), access: perm(PERM.forms) },
  { pattern: compile('/clinic/prescription-print'), access: perm(PERM.printSettings) },
  { pattern: compile('/clinic/invoice-print'), access: perm(PERM.printSettings) },
  { pattern: compile('/clinic/purchase-order-print'), access: perm(PERM.printSettings) },
  { pattern: compile('/clinic/charting-templates'), access: perm(PERM.chartingTemplates) },
  { pattern: compile('/clinic/patient-workspace'), access: perm(PERM.patientWorkspace) },
  { pattern: compile('/clinic/outstanding'), access: perm(PERM.patientWorkspace) },
  // PRD.302 — Queue Settings is clinic configuration, so it reuses the existing
  // Clinic Settings permission (Admin-only in the seed CSV) rather than the
  // broader `Queue Module | Queue` access that operational users have. Must be
  // listed BEFORE `/clinic/queue` — rules match in order.
  { pattern: compile('/clinic/queue/settings'), access: perm(PERM.clinicSettings) },
  { pattern: compile('/clinic/queue'), access: perm(PERM.queue) },
  // PRD.305 — Laboratory Settings is clinic configuration, so it reuses the
  // Clinic Settings permission (Admin-only in the seed CSV) rather than the
  // operational `Laboratory | Lab Orders` access.
  { pattern: compile('/clinic/laboratory/settings'), access: perm(PERM.clinicSettings) },

  // Patient creation
  { pattern: compile('/patient/new'), access: perm(PERM.createPatient) },
  { pattern: compile('/patient/register'), access: perm(PERM.createPatient) },

  // Prescription templates — no confident CSV row (see report). Fail closed.
  { pattern: compile('/patients/prescription-templates'), access: UNMAPPED },
  // Patient LIST pages only need to read patient records — not the workspace.
  // Roles without Patient Workspace (e.g. front desk) browse the list and open
  // a read-only details drawer instead of navigating into /patient/:id.
  { pattern: compile('/patients/today'), access: perm(PERM.patientProfile) },
  { pattern: compile('/patients/favorites'), access: perm(PERM.patientProfile) },
  { pattern: compile('/patients'), access: perm(PERM.patientProfile) },

  // Patient detail workspace (default: Patient Module / Patient Workspace)
  { pattern: compile('/patient/:id/consultation'), access: perm(PERM.consultation) },
  { pattern: compile('/patient/:id/pharmacy'), access: perm(PERM.pharmacy) },
  { pattern: compile('/patient/:id/packages'), access: perm(PERM.patientPackages) },
  // PRD.286 — Files & Images. RBAC gating intentionally deferred for now; any
  // authenticated user who can reach the patient can open the file repository.
  { pattern: compile('/patient/:id/files'), access: AUTH_ONLY },
  // PRD.305 — Patient Workspace Labs. Reading laboratory work is the entry
  // requirement; every ACTION inside is gated further by Laboratory Settings
  // mode ∩ RBAC via `resolveLaboratoryCapabilities`.
  { pattern: compile('/patient/:id/labs'), access: perm(PERM.labOrders) },
  { pattern: compile('/patient/:id/edit/:group'), access: perm(PERM.patientWorkspaceEdit) },
  {
    pattern: compile('/patient/:id/encounters/:encounterId/forms/:formInstanceId'),
    access: perm(PERM.patientWorkspace),
  },
  { pattern: compile('/patient/:id/encounters/:encounterId'), access: perm(PERM.patientWorkspace) },
  { pattern: compile('/patient/:id/encounters'), access: perm(PERM.patientWorkspace) },
  { pattern: compile('/patient/:id/appointments'), access: perm(PERM.patientWorkspace) },
  { pattern: compile('/patient/:id'), access: perm(PERM.patientWorkspace) },

  // Inventory
  {
    pattern: compile('/inventory/items/new'),
    access: perm(req(MODULES.INVENTORY, 'Inventory Items', 'edit')),
  },
  {
    pattern: compile('/inventory/items/:id/edit'),
    access: perm(req(MODULES.INVENTORY, 'Inventory Items', 'edit')),
  },
  { pattern: compile('/inventory/items'), access: perm(PERM.inventoryItems) },
  {
    pattern: compile('/inventory/adjustments/new'),
    access: perm(req(MODULES.INVENTORY, 'Inventory Adjustments', 'edit')),
  },
  { pattern: compile('/inventory/adjustments'), access: perm(PERM.inventoryAdjustments) },
  { pattern: compile('/inventory/suppliers'), access: perm(PERM.suppliers) },
  {
    pattern: compile('/inventory/purchase-orders/new'),
    access: perm(req(MODULES.INVENTORY, 'Purchase Orders', 'edit')),
  },
  {
    pattern: compile('/inventory/purchase-orders/:purchaseOrderId/receive'),
    access: perm(req(MODULES.INVENTORY, 'Delivery Orders', 'edit')),
  },
  { pattern: compile('/inventory/purchase-orders'), access: perm(PERM.purchaseOrders) },
  { pattern: compile('/inventory/purchase-order-print'), access: perm(PERM.purchaseOrderPrint) },
  {
    pattern: compile('/inventory/delivery-orders/direct/new'),
    access: perm(req(MODULES.INVENTORY, 'Delivery Orders', 'edit')),
  },
  {
    pattern: compile('/inventory/delivery-orders/direct/:deliveryOrderId'),
    access: perm(PERM.deliveryOrders),
  },
  { pattern: compile('/inventory/delivery-orders'), access: perm(PERM.deliveryOrders) },
  { pattern: compile('/inventory/stock-card'), access: perm(PERM.stockCard) },
  { pattern: compile('/inventory/pharmacy'), access: perm(PERM.pharmacy) },
  { pattern: compile('/inventory/invoices'), access: perm(PERM.billing) },
  { pattern: compile('/inventory/patient-packages'), access: perm(PERM.patientPackages) },
  { pattern: compile('/inventory'), access: perm(PERM.inventoryItems) },

  // PRD.305 — Laboratory module (operational queue).
  // The lab service catalogue is the same `appointment_services` rows the
  // Appointments Services page edits, reached from a second place. Either
  // module's permission grants it: a lab manager maintaining their test list
  // should not need Appointments rights, and someone who already administers
  // services should not lose the page because they lack Laboratory.
  {
    pattern: compile('/laboratory/services'),
    access: anyPerm(PERM.labOrders, PERM.manageServices),
  },
  // Same reasoning for the Lab Work form catalogue: these are the
  // `form_definitions` the Forms builder owns, listed again for the people who
  // assign them to tests. Either module's permission opens it.
  {
    pattern: compile('/laboratory/forms/:formId'),
    access: anyPerm(PERM.labOrders, PERM.forms),
  },
  { pattern: compile('/laboratory/forms'), access: anyPerm(PERM.labOrders, PERM.forms) },
  { pattern: compile('/laboratory'), access: perm(PERM.labOrders) },

  // Appointments
  { pattern: compile('/appointments/new'), access: perm(PERM.bookAppointment) },
  { pattern: compile('/appointments/month'), access: perm(PERM.monthView) },
  { pattern: compile('/appointments/week'), access: perm(PERM.weekView) },
  { pattern: compile('/appointments/day'), access: perm(PERM.dayView) },
  { pattern: compile('/appointments/list'), access: perm(PERM.listView) },
  { pattern: compile('/appointments/providers'), access: perm(PERM.manageProviders) },
  {
    pattern: compile('/appointments/provider-availability'),
    access: perm(PERM.providerAvailability),
  },
  { pattern: compile('/appointments/services'), access: perm(PERM.manageServices) },
  { pattern: compile('/appointments/settings/calendar'), access: perm(PERM.calendarSettings) },
  { pattern: compile('/appointments/settings/categories'), access: perm(PERM.categoryStatuses) },
  { pattern: compile('/appointments/settings/online-booking'), access: perm(PERM.onlineBooking) },
  {
    pattern: compile('/appointments/settings/front-desk-booking'),
    access: perm(PERM.frontDeskBooking),
  },
];

/**
 * Resolve the access requirement for a concrete pathname.
 * Returns `{ kind: 'unmapped' }` for protected routes with no confident
 * mapping (caller must fail closed and emit a dev warning).
 */
export function accessForPath(pathname: string): RouteAccess {
  for (const rule of RULES) {
    if (rule.pattern.test(pathname)) return rule.access;
  }
  return UNMAPPED;
}

/**
 * Whether a destination path is accessible to a user, given a `canAccess`
 * predicate. Used to gate launcher tiles and sidebar nav items by their
 * route (single source of truth with the route guards). Fails closed for
 * unmapped destinations.
 */
export function isPathAccessible(
  pathname: string,
  can: (requirement: PermissionRequirement) => boolean,
): boolean {
  const access = accessForPath(pathname);
  switch (access.kind) {
    case 'public':
    case 'authOnly':
      return true;
    case 'permission':
      return can(access.requirement);
    case 'anyPermission':
      return access.requirements.some(can);
    default:
      return false;
  }
}
